import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  alpha,
  styled,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockIcon from '@mui/icons-material/Lock';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { useRoles, useDeleteRole, useCloneRole } from '../../hooks/useRoles';
import type { Role, Organization } from '../../types';

const fetchOrganizations = async (): Promise<Organization[]> => {
  const response = await axiosInstance.get('/organizations');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const StyledCard = styled(Card)(() => ({
  background: 'linear-gradient(to bottom right, #ffffff, #fafbfc)',
  borderRadius: '16px',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  },
}));

const HeaderBox = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
  padding: '48px 40px',
  borderRadius: '16px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(124, 58, 237, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '40%',
    height: '200%',
    background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.03), transparent)',
    transform: 'rotate(15deg)',
    pointerEvents: 'none',
  },
}));

const SearchField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#fafbfc',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: '0 0 0 4px rgba(124, 58, 237, 0.12)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '2px solid transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha('#7c3aed', 0.3),
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7c3aed',
  },
}));

const ActionButton = styled(IconButton)(() => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

export const RoleList = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  // Fetch organizations for the dropdown
  const { data: organizations, isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: fetchOrganizations,
  });

  // Fetch roles for selected organization
  const { data: roles, isLoading: isLoadingRoles } = useRoles(selectedOrgId);

  const deleteMutation = useDeleteRole();
  const cloneMutation = useCloneRole();

  const handleDeleteClick = (role: Role) => {
    if (role.isSystemDefault) {
      showMessage('Cannot delete system default roles', 'error');
      return;
    }
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (roleToDelete) {
      deleteMutation.mutate(roleToDelete.id, {
        onSuccess: () => {
          showMessage('Role deleted successfully', 'success');
          setDeleteDialogOpen(false);
          setRoleToDelete(null);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to delete role', 'error');
        },
      });
    }
  };

  const handleClone = (role: Role) => {
    if (!selectedOrgId) return;
    cloneMutation.mutate(
      { id: role.id, organizationId: selectedOrgId },
      {
        onSuccess: (newRole) => {
          showMessage(`Role "${role.name}" cloned successfully`, 'success');
          navigate(`/roles/edit/${newRole.id}`);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to clone role', 'error');
        },
      }
    );
  };

  const filteredRoles =
    roles?.filter((role: Role) =>
      searchQuery
        ? role.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          role.description?.toLowerCase().includes(searchQuery.toLowerCase())
        : true
    ) || [];

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Role Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {params.row.isSystemDefault && (
            <LockIcon sx={{ fontSize: 16, color: '#697386' }} />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#1a1f36',
              fontSize: '14px',
            }}
          >
            {params.value}
          </Typography>
          {params.row.isSystemDefault && (
            <Chip
              label="System"
              size="small"
              sx={{
                fontSize: '10px',
                height: '20px',
                backgroundColor: alpha('#6366f1', 0.1),
                color: '#6366f1',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: '#697386',
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'rolePermissions',
      headerName: 'Permissions',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={`${params.value?.length || 0} permissions`}
          size="small"
          sx={{
            fontSize: '11px',
            backgroundColor: alpha('#10b981', 0.1),
            color: '#10b981',
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ActionButton
            size="small"
            onClick={() => navigate(`/roles/edit/${params.row.id}`)}
            sx={{
              color: '#4CAF50',
              '&:hover': {
                backgroundColor: alpha('#4CAF50', 0.1),
              },
            }}
          >
            <EditIcon fontSize="small" />
          </ActionButton>
          <ActionButton
            size="small"
            onClick={() => handleClone(params.row)}
            disabled={cloneMutation.isPending}
            sx={{
              color: '#7c3aed',
              '&:hover': {
                backgroundColor: alpha('#7c3aed', 0.1),
              },
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </ActionButton>
          <ActionButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            disabled={params.row.isSystemDefault}
            sx={{
              color: params.row.isSystemDefault ? '#ccc' : '#f44336',
              '&:hover': {
                backgroundColor: params.row.isSystemDefault ? 'transparent' : alpha('#f44336', 0.1),
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </ActionButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderBox>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '36px',
                letterSpacing: '-0.02em',
              }}
            >
              Roles & Permissions
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '15px',
              maxWidth: '600px',
            }}
          >
            Manage custom roles and permission sets for organizations
          </Typography>
        </Box>
      </HeaderBox>

      {/* Organization Selector */}
      <Box sx={{ mb: 3 }}>
        <TextField
          select
          label="Select Organization"
          value={selectedOrgId || ''}
          onChange={(e) => setSelectedOrgId(e.target.value || null)}
          fullWidth
          sx={{ maxWidth: 400 }}
          disabled={isLoadingOrgs}
        >
          <MenuItem value="">
            <em>Select an organization...</em>
          </MenuItem>
          {organizations?.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {!selectedOrgId ? (
        <Alert severity="info" sx={{ borderRadius: '12px' }}>
          Please select an organization to view and manage its roles.
        </Alert>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <SearchField
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: 1, maxWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#697386' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/roles/create?organizationId=${selectedOrgId}`)}
              sx={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                borderRadius: '10px',
                padding: '10px 24px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                boxShadow: '0 4px 14px 0 rgba(124, 58, 237, 0.35)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)',
                  boxShadow: '0 6px 20px 0 rgba(124, 58, 237, 0.45)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Create Role
            </Button>
          </Box>

          <StyledCard>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <DataGrid
                rows={filteredRoles}
                columns={columns}
                rowCount={filteredRoles.length}
                loading={isLoadingRoles}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                autoHeight
                disableRowSelectionOnClick
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#fafbfc',
                    borderBottom: '2px solid #e3e8ef',
                    borderRadius: 0,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 700,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#697386',
                  },
                  '& .MuiDataGrid-row': {
                    borderBottom: '1px solid #e3e8ef',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#7c3aed', 0.03),
                      cursor: 'pointer',
                    },
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: 'none',
                    padding: '16px',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e3e8ef',
                    backgroundColor: '#fafbfc',
                  },
                }}
              />
            </CardContent>
          </StyledCard>
        </>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', color: '#1a1f36' }}>
          Delete Role
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#697386' }}>
            Are you sure you want to delete the role "{roleToDelete?.name}"? Users with this role
            will lose these permissions.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#697386',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            sx={{
              textTransform: 'none',
              backgroundColor: '#f44336',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '8px',
              padding: '8px 20px',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
            }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
